import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Input from '../../components/ui/Input';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';
import { downloadFileFromUrl } from '../../lib/downloadFile';
import { KYC_OTHER_DOCUMENT_TAGS, kycOtherDocumentTagSchema, kycFileSchema } from '../../lib/validators/kycSchema';
import KycTagSelect from '../../components/kyc/KycTagSelect';
import { ClockIcon, DownloadIcon, PlusIcon, TrashIcon, UploadIcon } from '../../components/ui/icons';
import {
  useAdminListKycTemplatesQuery,
  useAdminGetKycTemplateQuery,
  useAdminCreateKycTemplateMutation,
  useAdminReplaceKycTemplateMutation,
  useAdminDeleteKycTemplateMutation,
} from '../../features/kyc/kycTemplatesApi';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * A real Button, not a plain `<a>` — a plain anchor either 401s (if
 * download_url is a protected route the anonymous nav can't carry the
 * Bearer token to) or just previews the file in a new tab instead of
 * saving it (browsers ignore the `download` attribute cross-origin). See
 * lib/downloadFile.js.
 */
function TemplateDownloadButton({ url, filename, children, className, size = 'sm', variant = 'secondary' }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const handleDownload = async () => {
    setBusy(true);
    const result = await downloadFileFromUrl(url, filename);
    setBusy(false);
    if (!result.ok) {
      toast.push({ tone: 'danger', title: 'Download failed', message: "Couldn't download this file. Please try again." });
    }
  };

  return (
    <Button type="button" size={size} variant={variant} isLoading={busy} onClick={handleDownload} className={className}>
      <DownloadIcon className="h-3.5 w-3.5" />
      {children ?? 'Download'}
    </Button>
  );
}

// `title` confirmed live against POST /admin/kyc/templates — a 422 came
// back with `{ errors: { title: ["The title field is required."] } }`,
// so this is real, not assumed like the rest of this file's shapes. Kept
// as an internal RHF field name matching the wire field 1:1 to avoid any
// future label/title drift.
const createTemplateSchema = z.object({
  tag: kycOtherDocumentTagSchema,
  title: z.string().trim().min(1, 'Enter a display title'),
  file: kycFileSchema,
});

const replaceTemplateSchema = z.object({
  file: kycFileSchema,
});

/**
 * v1.18.0 — admin Template Management. Gated by the `manage-kyc-documents`
 * role check at the route level (RoleRoute) — functionally identical to
 * every other admin gate in this app (admin OR super-admin) since there is
 * no more granular `permissions` signal available client-side (see
 * selectUserRoles), but named/commented separately because the spec calls
 * this out as an independent permission from manage-investors etc.
 *
 * Endpoint shapes are unverified against a live response this session —
 * see kycTemplatesApi.js's header for the full caveat.
 */
export default function KycTemplates() {
  const toast = useToast();
  const { data, isLoading } = useAdminListKycTemplatesQuery();
  const [createTemplate, { isLoading: isCreating }] = useAdminCreateKycTemplateMutation();
  const [replaceTemplate, { isLoading: isReplacing }] = useAdminReplaceKycTemplateMutation();
  const [deleteTemplate, { isLoading: isDeleting }] = useAdminDeleteKycTemplateMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [replaceTag, setReplaceTag] = useState(null);
  const [historyTag, setHistoryTag] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // template object | null

  const templates = data?.templates ?? [];
  const liveTemplates = templates.filter((t) => !t.isDeleted);
  // Cross-checked against the list endpoint per spec: only offer tags that
  // don't already have a current (non-deleted) template — the 422 is the
  // backstop for anything this list misses (unrecognized tags, races).
  const takenTags = new Set(liveTemplates.map((t) => t.tag));
  const availableKnownTags = KYC_OTHER_DOCUMENT_TAGS.filter((t) => !takenTags.has(t.value));

  const createForm = useForm({
    resolver: zodResolver(createTemplateSchema),
    defaultValues: { tag: '', title: '', file: undefined },
  });

  const replaceForm = useForm({ resolver: zodResolver(replaceTemplateSchema), defaultValues: { file: undefined } });

  const { data: historyTemplate, isLoading: isLoadingHistory } = useAdminGetKycTemplateQuery(historyTag, {
    skip: !historyTag,
  });

  const closeCreate = () => {
    setCreateOpen(false);
    createForm.reset({ tag: '', title: '', file: undefined });
  };

  const onCreate = async (values) => {
    try {
      const formData = new FormData();
      formData.append('tag', values.tag);
      formData.append('title', values.title);
      formData.append('file', values.file);
      await createTemplate(formData).unwrap();
      toast.push({ tone: 'success', message: 'Template created.' });
      closeCreate();
    } catch (err) {
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => createForm.setError(field, { message: messages[0] }));
        return;
      }
      createForm.setError('root', { message: authErrorMessage(err, { fallback: 'Could not create this template.' }) });
    }
  };

  const closeReplace = () => {
    setReplaceTag(null);
    replaceForm.reset({ file: undefined });
  };

  const onReplace = async (values) => {
    try {
      const formData = new FormData();
      formData.append('file', values.file);
      await replaceTemplate({ tag: replaceTag, formData }).unwrap();
      toast.push({ tone: 'success', message: 'New version uploaded. Previous versions are kept in history.' });
      closeReplace();
    } catch (err) {
      replaceForm.setError('root', { message: authErrorMessage(err, { fallback: 'Could not upload a new version.' }) });
    }
  };

  const runDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTemplate(deleteTarget.tag).unwrap();
      toast.push({ tone: 'success', message: `"${deleteTarget.label}" removed from investor checklists.` });
      setDeleteTarget(null);
    } catch (err) {
      toast.push({
        tone: 'danger',
        title: 'Delete failed',
        message: authErrorMessage(err, { fallback: 'Could not delete this template.' }),
      });
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">KYC document templates</h1>
          <p className="text-foreground-muted text-sm mt-1">
            Downloadable forms investors fill out and re-upload. Nothing here is ever deleted — old versions stay in
            history, and removing a tag only drops it from investors' checklists going forward.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <PlusIcon className="h-4 w-4" />
          Add template
        </Button>
      </div>

      <Card>
        {isLoading && <SkeletonTable columns={['Tag', 'Current version', 'Versions', 'Updated', '']} rows={3} />}

        {!isLoading && liveTemplates.length === 0 && (
          <p className="py-10 text-center text-foreground-muted">No templates yet — add one to start building investors' checklists.</p>
        )}

        {!isLoading && liveTemplates.length > 0 && (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-foreground-subtle border-b border-border">
                    <th className="py-2 pr-4 font-medium">Tag</th>
                    <th className="py-2 pr-4 font-medium">Current version</th>
                    <th className="py-2 pr-4 font-medium">Versions</th>
                    <th className="py-2 pr-4 font-medium">Updated</th>
                    <th className="py-2 pr-4 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {liveTemplates.map((t) => (
                    <tr key={t.tag} className="border-b border-border last:border-0">
                      <td className="py-3 pr-4">
                        <p className="text-foreground font-medium">{t.label}</p>
                        <p className="text-xs text-foreground-subtle">{t.tag}</p>
                      </td>
                      <td className="py-3 pr-4">
                        {t.currentVersion?.downloadUrl ? (
                          <div>
                            <p className="text-xs text-foreground-muted truncate max-w-[180px]">{t.currentVersion.fileName}</p>
                            <TemplateDownloadButton url={t.currentVersion.downloadUrl} filename={t.currentVersion.fileName} className="mt-1">
                              Download
                            </TemplateDownloadButton>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <button type="button" className="text-xs text-accent hover:underline inline-flex items-center gap-1" onClick={() => setHistoryTag(t.tag)}>
                          <ClockIcon className="h-3.5 w-3.5" />
                          {t.versionCount} version{t.versionCount === 1 ? '' : 's'}
                        </button>
                      </td>
                      <td className="py-3 pr-4 text-foreground-muted text-xs">{formatDate(t.updatedAt)}</td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2 justify-end">
                          <Button size="sm" variant="secondary" onClick={() => setReplaceTag(t.tag)}>
                            <UploadIcon className="h-3.5 w-3.5" />
                            Replace
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => setDeleteTarget(t)}>
                            <TrashIcon className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {liveTemplates.map((t) => (
                <div key={t.tag} className="rounded-xl border border-border bg-background-elevated p-4 space-y-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-foreground font-medium truncate">{t.label}</p>
                      <p className="text-xs text-foreground-subtle truncate">{t.tag}</p>
                    </div>
                    <Badge tone="neutral">{t.versionCount} ver.</Badge>
                  </div>
                  {t.currentVersion?.downloadUrl && (
                    <TemplateDownloadButton url={t.currentVersion.downloadUrl} filename={t.currentVersion.fileName} className="w-full">
                      Download {t.currentVersion.fileName}
                    </TemplateDownloadButton>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <Button size="sm" variant="secondary" className="flex-1" onClick={() => setReplaceTag(t.tag)}>
                      Replace
                    </Button>
                    <Button size="sm" variant="secondary" className="flex-1" onClick={() => setHistoryTag(t.tag)}>
                      History
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => setDeleteTarget(t)}>
                      <TrashIcon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Add Template */}
      <Modal open={createOpen} onClose={closeCreate} title="Add template">
        <form onSubmit={createForm.handleSubmit(onCreate)} className="space-y-4" noValidate>
          <Controller
            name="tag"
            control={createForm.control}
            render={({ field }) => (
              <KycTagSelect
                value={field.value}
                onChange={field.onChange}
                knownTags={availableKnownTags}
                error={createForm.formState.errors.tag?.message}
              />
            )}
          />
          <Input label="Display title" placeholder="e.g. Fixed Income Agreement" error={createForm.formState.errors.title?.message} {...createForm.register('title')} />
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">Template file</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/jpg,application/pdf"
              onChange={(e) => createForm.setValue('file', e.target.files?.[0], { shouldValidate: true })}
              className="w-full text-sm text-foreground-muted file:mr-3 file:rounded-lg file:border-0 file:bg-background-elevated file:px-3 file:py-2 file:text-sm file:font-medium file:text-foreground file:cursor-pointer hover:file:bg-background-surface"
            />
            {createForm.formState.errors.file && <p className="mt-1.5 text-xs text-danger">{createForm.formState.errors.file.message}</p>}
          </div>
          {createForm.formState.errors.root && <p className="text-sm text-danger">{createForm.formState.errors.root.message}</p>}
          <Button type="submit" className="w-full" isLoading={isCreating}>
            Create template
          </Button>
        </form>
      </Modal>

      {/* Replace / new version */}
      <Modal open={!!replaceTag} onClose={closeReplace} title="Upload new version">
        <form onSubmit={replaceForm.handleSubmit(onReplace)} className="space-y-4" noValidate>
          <p className="text-sm text-foreground-muted">
            This becomes the new current version. The previous version stays available in history — nothing is deleted.
          </p>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">New file</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/jpg,application/pdf"
              onChange={(e) => replaceForm.setValue('file', e.target.files?.[0], { shouldValidate: true })}
              className="w-full text-sm text-foreground-muted file:mr-3 file:rounded-lg file:border-0 file:bg-background-elevated file:px-3 file:py-2 file:text-sm file:font-medium file:text-foreground file:cursor-pointer hover:file:bg-background-surface"
            />
            {replaceForm.formState.errors.file && <p className="mt-1.5 text-xs text-danger">{replaceForm.formState.errors.file.message}</p>}
          </div>
          {replaceForm.formState.errors.root && <p className="text-sm text-danger">{replaceForm.formState.errors.root.message}</p>}
          <Button type="submit" className="w-full" isLoading={isReplacing}>
            Upload new version
          </Button>
        </form>
      </Modal>

      {/* Version history */}
      <Modal open={!!historyTag} onClose={() => setHistoryTag(null)} title={historyTemplate?.label || 'Version history'}>
        {isLoadingHistory && <p className="py-6 text-center text-foreground-muted">Loading…</p>}
        {!isLoadingHistory && historyTemplate && (
          <ol className="relative border-l border-border ml-2 space-y-4 py-1">
            {(historyTemplate.versions ?? []).map((v) => (
              <li key={v.id} className="relative ml-5">
                <span className={`absolute -left-[27px] top-1 h-2.5 w-2.5 rounded-full ring-4 ring-background ${v.isCurrent ? 'bg-accent' : 'bg-foreground-subtle'}`} />
                <div className="flex flex-wrap items-center gap-2">
                  {v.isCurrent && <Badge tone="success">Current</Badge>}
                  <span className="text-sm text-foreground">{v.fileName}</span>
                  <span className="text-xs text-foreground-subtle">{formatDate(v.uploadedAt)}</span>
                </div>
                {v.uploadedBy && <p className="text-[11px] text-foreground-subtle mt-0.5">By {v.uploadedBy}</p>}
                {v.downloadUrl && (
                  <div className="mt-1.5">
                    <TemplateDownloadButton url={v.downloadUrl} filename={v.fileName} />
                  </div>
                )}
              </li>
            ))}
            {(!historyTemplate.versions || historyTemplate.versions.length === 0) && (
              <p className="text-sm text-foreground-muted">No version history available.</p>
            )}
          </ol>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={runDelete}
        isLoading={isDeleting}
        tone="danger"
        title="Delete this template?"
        confirmLabel="Delete template"
        message={
          deleteTarget
            ? `This removes "${deleteTarget.label}" from every investor's Other Documents checklist and from the completion-percentage calculation going forward. The file and its version history are kept — nothing already uploaded against this tag is deleted, and it can't be un-deleted from here if you change your mind, so a new template would need to be created again.`
            : ''
        }
      />
    </div>
  );
}
